import { XMarkIcon } from '@heroicons/react/24/outline';
import React, { useState, type FC } from 'react';

import { useCreateTag } from '@/api/queries/tag';
import type { Tag } from '@/api/types/tags';
import { Button, ButtonSize, ButtonType } from '@/common/button';
import { Modal } from '@/common/modal';
import { TextField, TextFieldType } from '@/common/text-field';

interface CreateTagModalProps {
    isOpen: boolean;
    onCreate?: (id: number) => void;
    setIsOpen: (isOpen: boolean) => void;
}

export const CreateTagModal: FC<CreateTagModalProps> = ({ isOpen, setIsOpen, onCreate }) => {
    const [tagName, setTagName] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const { mutate: createTag } = useCreateTag();

    return (
        <Modal isOpen={isOpen}>
            <div className="flex flex-col absolute min-w-96 w-[38vw] max-w-[650px] bg-gray-150 shadow-xl rounded-xl transition-all duration-300 ease-in-out">
                <div className="flex flex-row justify-between p-4 items-center">
                    <p className="text-xl text-white">Create a new tag</p>
                    <XMarkIcon
                        className="h-5 w-5 text-white hover:cursor-pointer hover:text-gray-30"
                        onClick={isLoading ? () => {} : () => setIsOpen(false)}
                    />
                </div>
                <hr className="bg-gray-100 border-gray-120" />
                <div className="flex flex-col">
                    <div className="flex flex-col p-4">
                        <p className="ml-1 mb-1 text-white text-sm">Name</p>
                        <TextField
                            id="tag-name"
                            required={true}
                            type={TextFieldType.Text}
                            value={tagName}
                            placeholder="Tag Name"
                            onChange={(value) => {
                                setTagName(value);
                            }}
                        />
                    </div>
                    <hr className="mt-2 bg-gray-100 border-gray-120" />
                    <div className="w-full flex flex-row justify-end gap-3 p-6">
                        <Button
                            title="Cancel"
                            className="hover:bg-gray-160"
                            buttonType={ButtonType.SECONDARY}
                            buttonSize={ButtonSize.LARGE}
                            disabled={isLoading}
                            onClick={() => setIsOpen(false)}
                        />
                        <Button
                            title="Done"
                            buttonType={ButtonType.PRIMARY}
                            buttonSize={ButtonSize.LARGE}
                            disabled={!tagName || isLoading}
                            onClick={async () => {
                                createTag(tagName, {
                                    onSuccess: (data) => {
                                        const tag = data as Tag;
                                        setTagName('');
                                        setIsOpen(false);
                                        onCreate?.(tag.id);
                                    },
                                });
                            }}
                        />
                    </div>
                </div>
            </div>
        </Modal>
    );
};
